import HelpTooltip from '@components/Tooltips/HelpTooltip';
import { FormControl, Autocomplete, TextField, Popper } from '@mui/material';
import React, { ComponentProps, useCallback, useRef } from 'react';

type Props = {
	// eslint-disable-next-line react/require-default-props
	label?: string;
	setValue: (value: string) => void;
	options: string[] | { label: string; value: string }[];
	value: string;
	getOptionLabel: (option: string) => string;
	// eslint-disable-next-line react/require-default-props
	order?: number;
	// eslint-disable-next-line react/require-default-props
	helpTooltip?: string;
	// eslint-disable-next-line react/require-default-props
	popperProps?: Partial<PopperProps>;
};

type PopperProps = ComponentProps<typeof Popper>;

const AutocompleteControl: React.FC<Props> = ({
	options = [],
	value,
	setValue,
	label,
	order,
	helpTooltip,
	popperProps,
	...props
}: Props) => {
	const onChange = useCallback(
		(_: any, v: string | { label: string; value: string }) => {
			if (v) setValue(typeof v === 'string' ? v : v.value);
		},
		[setValue]
	);

	const rootRef = useRef<any>();

	// we need this to make sure the popover is in the correct width when there is a help button
	const CustomPopper = React.useCallback(
		// eslint-disable-next-line @typescript-eslint/no-shadow
		(props: ComponentProps<typeof Popper>) => (
			<Popper
				{...props}
				anchorEl={rootRef.current}
				style={{ width: rootRef.current?.clientWidth }}
				placement="bottom"
				{...popperProps}
			/>
		),
		[popperProps]
	);

	return (
		<FormControl
			ref={rootRef}
			fullWidth
			margin="normal"
			component="fieldset"
			sx={{
				order,
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'center'
			}}
		>
			<Autocomplete
				{...props}
				PopperComponent={CustomPopper}
				autoSelect
				blurOnSelect
				filterSelectedOptions={false}
				disableCloseOnSelect={false}
				value={value as string}
				disablePortal
				disableClearable
				renderInput={(params: {}) => (
					<TextField
						type="text"
						{...params}
						label={label}
						InputLabelProps={{ shrink: true }}
					/>
				)}
				options={options as string[]}
				onChange={onChange}
			/>
			{helpTooltip && (
				<HelpTooltip title={helpTooltip} placement="top-end" sx={{ ml: 1 }} />
			)}
		</FormControl>
	);
};

const MemoAutocompleteControl = React.memo(AutocompleteControl);

export { MemoAutocompleteControl as AutocompleteControl };
